import type {
	ParametricListInfo,
	Pricelist
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

interface ProductStore {
	addParametricsInformation: (value: ParametricListInfo[]) => void;
	addDefaultProductImage: (value: string) => void;
	setPriceLists: (value: Pricelist[]) => void;
}

interface ParallelProductInitDeps {
	getParametricsInformation: (ctx: object) => Promise<ParametricListInfo[]>;
	cartInit: () => Promise<unknown>;
	productStore: ProductStore;
	storeContext: object;
	defaultImageFilename: string;
	priceLists: Pricelist[];
}

/**
 * Runs getParametricsInformation and cartInit in parallel.
 *
 * Cart failure is isolated — logged via console.error and swallowed — so a
 * cart outage never blocks the product catalogue from initialising.
 * Parametrics failure IS re-thrown; the caller must handle it.
 *
 * Both promises are kicked off before either is awaited, preserving the
 * pagespeed win of parallel network requests.
 */
export async function parallelProductInit(
	deps: ParallelProductInitDeps
): Promise<void> {
	const {
		getParametricsInformation,
		cartInit,
		productStore,
		storeContext,
		defaultImageFilename,
		priceLists
	} = deps;

	// Start both requests simultaneously before awaiting either.
	const paramInfoPromise = getParametricsInformation({ ...storeContext });
	const cartPromise = cartInit().catch((error: unknown) => {
		console.error("[useInitApp] Cart init failed", error);
	});

	// Parametrics failure propagates intentionally — let the caller decide.
	const paramInfo = await paramInfoPromise;

	productStore.addParametricsInformation(paramInfo);
	productStore.addDefaultProductImage(defaultImageFilename);
	productStore.setPriceLists(priceLists);

	// Await the cart promise last so no unhandled-rejection warning fires.
	await cartPromise;
}
